'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { trackEvent, identifyUser } from '@/lib/mixpanel';

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

interface PasswordStrength {
  minLength: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

function checkPasswordStrength(password: string): PasswordStrength {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^A-Za-z0-9]/.test(password),
  };
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<'customer' | 'vendor'>('customer');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { signUp } = useAuth();
  const router = useRouter();

  const strength = checkPasswordStrength(password);
  const allStrong = Object.values(strength).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = email.length > 0 && allStrong && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setFormError('');
    try {
      const data = await signUp(email, password, { fullName: '', role });

      // Send welcome email (non-blocking)
      try {
        const supabase = createClient();
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'welcome',
            to: email,
            data: {
              email,
              role,
              name: email.split('@')[0],
            },
          },
        });
      } catch {
        // Welcome email failure is non-blocking — signup still succeeds
      }

      // Mixpanel: identify and track signup
      try {
        const userId = data?.user?.id;
        if (userId) {
          identifyUser(userId, {
            $email: email,
            $name: email.split('@')[0],
            role,
          });
        }
        trackEvent('User Signed Up', {
          method: 'email',
          role,
        });
      } catch {
        // tracking failure is non-blocking
      }

      // If Supabase returned a session, the user is immediately logged in
      if (data?.session) {
        router.push(role === 'vendor' ? '/vendor-dashboard' : '/homepage');
        router.refresh();
      } else {
        // Email confirmation is enabled — session won't exist until the user
        // clicks the confirmation link. Show a "check your email" message
        // instead of redirecting as a guest.
        setEmailSent(true);
      }
    } catch (err: any) {
      setFormError(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const StrengthRow = ({ met, label }: { met: boolean; label: string }) => (
    <div className="flex items-center gap-2">
      <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${met ? 'bg-green-500' : 'bg-muted border border-border'}`}>
        {met && <Icon name="CheckIcon" size={8} className="text-white" />}
      </div>
      <span className={`text-xs transition-colors ${met ? 'text-green-400' : 'text-muted-foreground'}`}>{label}</span>
    </div>
  );

  // Email confirmation pending state
  if (emailSent) {
    return (
      <div className="space-y-5 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center">
            <Icon name="EnvelopeIcon" size={32} className="text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Check your email</h1>
            <p className="text-sm text-muted-foreground mt-2">
              We sent a confirmation link to
            </p>
            <p className="text-sm font-semibold text-foreground mt-1">{email}</p>
          </div>
        </div>

        <div className="px-4 py-3 rounded-xl bg-accent/10 border border-accent/20 text-sm text-muted-foreground text-left">
          Click the link in the email to verify your account and get started. Check your spam folder if you don&apos;t see it.
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Already confirmed?{' '}
          <button type="button" onClick={onSwitchToLogin} className="text-accent font-semibold hover:underline">
            Sign in
          </button>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Create account</h1>
        <p className="text-sm text-muted-foreground mt-1">Join Prizma and start shopping</p>
      </div>

      {formError && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <Icon name="ExclamationCircleIcon" size={16} />
          {formError}
        </div>
      )}

      {/* Email */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="input-field w-full px-4 py-3 rounded-xl text-sm"
          required
        />
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Create a strong password"
            className="input-field w-full px-4 py-3 pr-11 rounded-xl text-sm"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>

        {/* Live strength validation */}
        {password.length > 0 && (
          <div className="grid grid-cols-2 gap-1.5 pt-1">
            <StrengthRow met={strength.minLength} label="8+ characters" />
            <StrengthRow met={strength.hasUppercase} label="1 uppercase letter" />
            <StrengthRow met={strength.hasNumber} label="1 number" />
            <StrengthRow met={strength.hasSpecial} label="1 special character" />
          </div>
        )}
      </div>

      {/* Confirm password */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirm Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          placeholder="Repeat your password"
          className="input-field w-full px-4 py-3 rounded-xl text-sm"
          required
          style={{
            borderColor: confirmPassword.length > 0 ? (passwordsMatch ? '#7BC67E' : '#E85D5D') : undefined,
          }}
        />
        {confirmPassword.length > 0 && !passwordsMatch && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={12} />
            Passwords do not match
          </p>
        )}
        {confirmPassword.length > 0 && passwordsMatch && (
          <p className="text-xs text-green-400 flex items-center gap-1">
            <Icon name="CheckCircleIcon" size={12} />
            Passwords match
          </p>
        )}
      </div>

      {/* Role selector */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">I want to</label>
        <div className="grid grid-cols-2 gap-3">
          {(['customer', 'vendor'] as const).map(r => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all"
              style={{
                borderColor: role === r ? 'var(--accent)' : 'var(--border)',
                backgroundColor: role === r ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : 'var(--card)',
              }}
            >
              <Icon
                name={r === 'customer' ? 'ShoppingBagIcon' : 'BuildingStorefrontIcon'}
                size={22}
                style={{ color: role === r ? 'var(--accent)' : 'var(--muted-foreground)' } as React.CSSProperties}
              />
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: role === r ? 'var(--accent)' : 'var(--muted-foreground)' }}>
                {r === 'customer' ? 'Shop' : 'Sell'}
              </span>
              <span className="text-[10px] text-muted-foreground text-center leading-tight">
                {r === 'customer' ? 'Browse & buy products' : 'List & manage products'}
              </span>
            </button>
          ))}
        </div>
      </div>

      <button
        type="submit"
        disabled={!canSubmit || loading}
        onClick={() => { if (canSubmit) trackEvent('Sign Up Button Clicked', { location: 'signup_form', role }); }}
        className="w-full btn-accent py-3.5 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Creating account...
          </>
        ) : (
          'Create Account'
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-accent font-semibold hover:underline">
          Sign in
        </button>
      </p>
    </form>
  );
}